'use strict';

/**
 * Modal « Fiche produit » showroom — ouverture / fermeture accessible (clavier + focus).
 *
 * Marquage Fluid :
 *   - [data-showroom-sheet-open] : bouton d’ouverture
 *   - [data-showroom-sheet-modal] : conteneur modal
 *   - [data-showroom-sheet-dismiss] : fermeture (backdrop / bouton)
 *   - [data-showroom-sheet-dialog] : panneau dialog
 */

const OPEN_SELECTOR = '[data-showroom-sheet-open]';
const MODAL_SELECTOR = '[data-showroom-sheet-modal]';
const DIALOG_SELECTOR = '[data-showroom-sheet-dialog]';
const DISMISS_SELECTOR = '[data-showroom-sheet-dismiss]';
const SALE_PRICE_INPUT_ID = 'tc-showroom-sale-price';
const FOCUSABLE_SELECTOR = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])',
].join(',');

/**
 * Initialise les modals showroom présents dans le document.
 *
 * @param {ParentNode} root
 */
function init(root = document) {
    const modals = root.querySelectorAll(MODAL_SELECTOR);
    if (!modals.length) {
        return;
    }

    modals.forEach((modal) => {
        if (!(modal instanceof HTMLElement) || modal.dataset.showroomSheetBound === '1') {
            return;
        }
        modal.dataset.showroomSheetBound = '1';
        bindModal(modal, root);
    });
}

/**
 * @param {HTMLElement} modal
 * @param {ParentNode} root
 */
function bindModal(modal, root) {
    const dialog = modal.querySelector(DIALOG_SELECTOR);
    if (!(dialog instanceof HTMLElement)) {
        return;
    }

    let lastFocus = null;

    /** @type {(event: KeyboardEvent) => void} */
    const onKeyDown = (event) => {
        if (modal.hasAttribute('hidden')) {
            return;
        }
        if (event.key === 'Escape') {
            event.preventDefault();
            closeModal(modal, lastFocus, onKeyDown);
            return;
        }
        if (event.key === 'Tab') {
            trapFocus(event, dialog);
        }
    };

    const openers = root.querySelectorAll(OPEN_SELECTOR);
    openers.forEach((opener) => {
        if (!(opener instanceof HTMLElement)) {
            return;
        }
        opener.addEventListener('click', (event) => {
            event.preventDefault();
            lastFocus = opener;
            openModal(modal, dialog, onKeyDown);
        });
    });

    modal.querySelectorAll(DISMISS_SELECTOR).forEach((dismiss) => {
        dismiss.addEventListener('click', (event) => {
            event.preventDefault();
            closeModal(modal, lastFocus, onKeyDown);
        });
    });
}

/**
 * @param {HTMLElement} modal
 * @param {HTMLElement} dialog
 * @param {(event: KeyboardEvent) => void} onKeyDown
 */
function openModal(modal, dialog, onKeyDown) {
    modal.removeAttribute('hidden');
    document.body.classList.add('tc-showroom-sheet-modal-open');
    document.addEventListener('keydown', onKeyDown);
    const input = document.getElementById(SALE_PRICE_INPUT_ID);
    if (input instanceof HTMLInputElement) {
        input.focus();
        return;
    }
    dialog.setAttribute('tabindex', '-1');
    dialog.focus();
}

/**
 * @param {HTMLElement} modal
 * @param {HTMLElement|null} lastFocus
 * @param {(event: KeyboardEvent) => void} onKeyDown
 */
function closeModal(modal, lastFocus, onKeyDown) {
    modal.setAttribute('hidden', '');
    document.body.classList.remove('tc-showroom-sheet-modal-open');
    document.removeEventListener('keydown', onKeyDown);
    if (lastFocus instanceof HTMLElement) {
        lastFocus.focus();
    }
}

/**
 * @param {KeyboardEvent} event
 * @param {HTMLElement} dialog
 */
function trapFocus(event, dialog) {
    const focusables = Array.from(dialog.querySelectorAll(FOCUSABLE_SELECTOR)).filter(
        (el) => el instanceof HTMLElement && el.offsetParent !== null,
    );
    if (focusables.length === 0) {
        event.preventDefault();
        return;
    }
    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    const active = document.activeElement;
    if (event.shiftKey && active === first) {
        event.preventDefault();
        last.focus();
        return;
    }
    if (!event.shiftKey && active === last) {
        event.preventDefault();
        first.focus();
    }
}

export default { init };
